import type { IconName } from "@/services/icons";

// The single owner of WHICH settings entries exist and who may see them.
//
// The menu is rendered twice — the Navbar accordion and the settings page tab
// bar — and used to be declared twice too, kept in sync only by a comment.
// They drifted: Consents was manager-gated in one place after being opened up
// in the other. Membership and gating live here; each consumer still picks its
// own label, because the two surfaces deliberately word them differently
// ("Metrics" in a narrow tab bar, "Calculated metrics" in the wider nav).
export interface SettingsMenuEntry {
  // Route name, and the stable identity of the entry across both surfaces.
  href: string;
  // Tab-bar label: short, for the horizontal strip on the settings page.
  tabName: string;
  // Nav label: the fuller wording the sidebar has room for.
  navName: string;
  icon: IconName;
  // Managers only. Not a paywall: MCP paywalls itself on the page, so the
  // entry stays visible to managers whether or not the org has bought it —
  // hiding it left managers with no way to discover the feature.
  managerOnly?: true;
}

export const SETTINGS_MENU: readonly SettingsMenuEntry[] = [
  {
    href: "general",
    tabName: "Organization",
    navName: "Organization",
    icon: "cog",
  },
  { href: "profile", tabName: "Profile", navName: "Profile", icon: "user" },
  { href: "tags", tabName: "Tags", navName: "Tags", icon: "tag" },
  {
    href: "resources",
    tabName: "Channels",
    navName: "Channels",
    icon: "layer-group",
  },
  {
    href: "edit-conversions",
    tabName: "Conversions",
    navName: "Edit conversions",
    icon: "file-edit",
  },
  {
    href: "calculated-metrics",
    tabName: "Metrics",
    navName: "Calculated metrics",
    icon: "plus-operator",
  },
  {
    href: "organization_settings_automations",
    tabName: "Automations",
    navName: "Automations",
    icon: "sparkles",
  },
  {
    href: "changelog",
    tabName: "Changelog",
    navName: "Changelog",
    icon: "user-clock",
  },
  // Everyone: the page itself explains that only a manager can accept or
  // withdraw, and a member still needs to read what each feature sends. The AI
  // sidepanel links members straight here.
  {
    href: "consent_settings",
    tabName: "Consents",
    navName: "Consents",
    icon: "handshake",
  },
  {
    href: "mcp-applications",
    tabName: "MCP",
    navName: "MCP Applications",
    icon: "plug",
    managerOnly: true,
  },
];

export function visibleSettingsEntries(
  isManager: boolean,
): readonly SettingsMenuEntry[] {
  return SETTINGS_MENU.filter((entry) => !entry.managerOnly || isManager);
}
